import type { ListBuilderConfiguration } from '../types';

/**
 * Client Notes list configuration
 * Converted from JSON to TypeScript for type safety
 */
export const CLIENT_NOTES_LIST_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'default-client-notes',
			cacheTTL: 600000,
			name: 'All Client Notes',
			type: 'default',
			active: true,
			columns: [
				{
					id: 'pin-toggle',
					fields: [
						{
							id: 'pin-toggle',
							displayStyle: 'pin_toggle',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'isPinned',
									objectTable: 'client_note',
								},
							},
							styleConfig: {
								size: 'md',
								colorRule: {
									type: 'default',
								},
							},
						},
					],
					headers: [
						{
							id: 'h-pin',
							text: '',
						},
					],
					width: 50,
					sortable: false,
				},
				{
					id: 'note-content',
					fields: [
						{
							id: 'content-field',
							displayStyle: 'text',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'content',
									objectTable: 'client_note',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
								textExpansion: {
									enabled: true,
									maxLines: 2,
								},
							},
						},
					],
					headers: [
						{
							id: 'h-content',
							text: 'Note',
						},
					],
					sortable: true,
					widthConfig: {
						mode: 'flexible',
						preset: 'xlarge',
					},
					verticalAlignment: 'top',
				},
				{
					// Combined "Created" column: creator name stacked above the
					// creation timestamp via a line_break field.
					id: 'created-column',
					fields: [
						{
							id: 'creator-field',
							displayStyle: 'text',
							dataConfig: {
								type: 'template',
								template: '{creatorFirstName} {creatorLastName}',
								templateFields: [],
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
							},
						},
						{
							id: 'created-line-break',
							displayStyle: 'line_break',
							dataConfig: {
								type: 'custom_text',
								customText: '',
							},
						},
						{
							id: 'created-date-field',
							displayStyle: 'date',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'createdDate',
									objectTable: 'client_note',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'muted',
								},
								formatOptions: {
									dateFormat: 'datetime-short',
								},
							},
						},
					],
					headers: [
						{
							id: 'h-created',
							text: 'Created',
						},
					],
					sortable: true,
					sortFields: [
						{
							objectTable: 'client_note',
							fieldName: 'createdDate',
							direction: 'DESC',
						},
					],
					width: 120,
				},
				{
					// Combined "Last Modified" column: modifier name stacked
					// above the modification timestamp.
					id: 'modified-column',
					fields: [
						{
							id: 'modified-by-field',
							displayStyle: 'text',
							dataConfig: {
								type: 'template',
								template: '{modifierFirstName} {modifierLastName}',
								templateFields: [],
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
							},
						},
						{
							id: 'modified-line-break',
							displayStyle: 'line_break',
							dataConfig: {
								type: 'custom_text',
								customText: '',
							},
						},
						{
							id: 'modified-date-field',
							displayStyle: 'date',
							dataConfig: {
								type: 'field',
								field: {
									// `dateTime` is the content-modification timestamp;
									// `_modified_date` would bump on pin/unpin too.
									fieldName: 'dateTime',
									objectTable: 'client_note',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'muted',
								},
								formatOptions: {
									dateFormat: 'datetime-short',
								},
							},
						},
					],
					headers: [
						{
							id: 'h-modified',
							text: 'Last Modified',
						},
					],
					sortable: true,
					sortFields: [
						{
							objectTable: 'client_note',
							fieldName: 'dateTime',
							direction: 'DESC',
						},
					],
					width: 120,
				},
				{
					id: 'actions-column',
					fields: [
						{
							id: 'actions-dropdown',
							dataConfig: {
								type: 'custom_text',
								customText: '',
							},
							styleConfig: {
								size: 'md',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'actions_dropdown',
							dropdownActions: [
								{
									id: 'edit-action',
									label: 'Edit',
									iconName: 'edit',
									callbackName: 'editNote',
									variant: 'primary',
									visibility: 'visible',
								},
								{
									id: 'delete-action',
									label: 'Delete',
									iconName: 'trash',
									callbackName: 'deleteNote',
									variant: 'danger',
									visibility: 'visible',
								},
							],
						},
					],
					headers: [
						{
							id: 'h-actions',
							text: '',
						},
					],
					width: 80,
					align: 'center',
					sortable: false,
				},
			],
			defaultSortId: 'last-modified-desc',
			sortFields: [
				{
					objectTable: 'client_note',
					fieldName: 'dateTime',
					direction: 'DESC',
				},
			],
			sortOrder: 0,
		},
	],
	paging: {
		defaultPageSize: 20,
		pageSizeOptions: [10, 20, 50, 100],
	},
	filters: {
		activeFilters: [
			'noteOrigin',
			'noteCreatedDateRange',
			'noteCreatedByUser',
			'noteModifiedDateRange',
			'noteModifiedByUser',
		],
		defaultFilters: {},
	},
	objects: [
		{
			objectLabel: 'Client Note',
			objectTable: 'client_note',
			selectedFields: [
				{
					fieldName: 'id',
					fieldLabel: 'ID',
				},
				{
					fieldName: 'content',
					fieldLabel: 'Content',
				},
				{
					fieldName: 'isPinned',
					fieldLabel: 'Is Pinned',
				},
				{
					fieldName: 'createdDate',
					fieldLabel: 'Created Date',
				},
				{
					fieldName: 'modifiedDate',
					fieldLabel: 'Modified Date',
				},
				{
					fieldName: 'dateTime',
					fieldLabel: 'Last Modified',
				},
				{
					fieldName: 'externalId',
					fieldLabel: 'External ID',
				},
				{
					fieldName: 'origin',
					fieldLabel: 'Origin',
				},
			],
		},
		{
			objectLabel: 'Creator',
			objectTable: 'user',
			selectedFields: [
				{
					fieldName: 'creatorFirstName',
					fieldLabel: 'First Name',
				},
				{
					fieldName: 'creatorLastName',
					fieldLabel: 'Last Name',
				},
			],
		},
		{
			objectLabel: 'Modifier',
			objectTable: 'user',
			selectedFields: [
				{
					fieldName: 'modifierFirstName',
					fieldLabel: 'First Name',
				},
				{
					fieldName: 'modifierLastName',
					fieldLabel: 'Last Name',
				},
			],
		},
	],
};
